/* eslint-disable default-param-last */
import { AllowedReducer, InterfaceReducer } from '@leverege/api-redux';
import { AbstractGroupDelegate } from '@leverege/molten/data-source';
import { RoutesUtil, History } from '@leverege/molten/routes';
import { GlobalState } from '@leverege/ui-redux';
import { Attributes } from '@leverege/ui-attributes';
import OpenApiApi from "../../openApiApi/OpenApiApi.js";
import OpenApiSortConverterFactory from "../../filters/sort/sortConverter/OpenApiSortConverterFactory.js"; // eslint-disable-next-line import/no-cycle
import OpenApiItemDelegate from './OpenApiItemDelegate.js';
import { OpenApiComparatorFilterConverterFactory, OpenApiConditionFilterConverterFactory } from "../../filters/filter/Factory.js";
export default class OpenApiGroupDelegate extends AbstractGroupDelegate {
  static TYPE = 'openApi.GroupDelegate';
  constructor(options) {
    super(options);
    this.type = options.delegateType ?? OpenApiGroupDelegate.TYPE;
    this.options = options;
    const {
      match,
      relationship
    } = options;
    this.match = match;
    this.relationship = relationship;
    this.objectType = this.relationship.objectType;
    this.api = new OpenApiApi({
      relationship,
      match
    });
    this.itemDelegateClass = OpenApiItemDelegate;
  }
  getApi() {
    return this.api;
  }
  item(id) {
    const iRel = this.relationship.createItem(this.match, id);
    const Clazz = this.itemDelegateClass;
    return new Clazz({
      match: iRel.match,
      relationship: iRel,
      id
    });
  }
  requestCanList(opts) {
    return this.getApi().canList(opts);
  }
  list(opts, extra) {
    return GlobalState.dispatch(this.getApi().list(opts, extra));
  }
  requestCanSearch(opts) {
    return this.getApi().canList(opts);
  }
  search(opts, extra) {
    return this.getApi().list(opts, extra);
  }
  getInterfaceUrl() {
    return this.relationship.ref;
  }
  getUrl() {
    return RoutesUtil.resolve(this.relationship.urlPath, this.match);
  }
  getResourceId(forReducer, opts) {
    const path = this.relationship.refPath;
    return forReducer ? RoutesUtil.resolve(this.relationship.urlPath, this.match) : path;
  }
  requestCanGet(id) {
    return this.item(id).requestCanGet();
  }
  get(id, opts, extra) {
    return this.item(id).get(opts, extra);
  }
  requestCanCreate(obj, options) {
    return this.getApi().canCreate(obj, options);
  }
  create(obj, options, extra) {
    return this.getApi().create(obj, options, extra);
  }
  requestCanUpdate(id, patch, options) {
    return this.item(id).requestCanUpdate(patch, options);
  }
  update(id, patch, options, extra) {
    return this.item(id).update(patch, options, extra);
  }
  requestCanDelete(id, deleteBody, options) {
    return this.item(id).requestCanDelete(deleteBody, options);
  }
  delete(id, deleteBody, options, extra) {
    return this.item(id).delete(deleteBody, options, extra);
  }

  // utility

  goTo(id) {
    if (id) {
      return this.item(id).goTo();
    }
    return History.push(`/${this.getUrl()}`);
  }
  isGotoAllowed() {
    return true;
  }

  // access

  getList(state = GlobalState.get(), opts) {
    return InterfaceReducer.getList(state, this.getResourceId(true, opts), opts, opts?.queryName);
  }
  getItem(id, state = GlobalState.get(), opts) {
    return this.item(id).getItem(state, opts);
  }
  getImagineGroup(id) {
    return this.item(id).getImagineGroup();
  }
  getImagineGroupItem(state = GlobalState.get(), id) {
    return this.item(id).getImagineGroupItem(state);
  }
  isLoading(state = GlobalState.get(), opts) {
    return InterfaceReducer.isLoading(state, this.getResourceId(true, opts));
  }
  isDone(state = GlobalState.get(), opts) {
    return InterfaceReducer.isDone(state, this.getResourceId(true, opts));
  }
  canList(state = GlobalState.get()) {
    if (!this.relationship.supportsList) {
      return false;
    }
    return AllowedReducer.get(state, this.getResourceId(true))?.get;
  }
  canUpdate(id, state = GlobalState.get()) {
    return this.item(id).canUpdate(state);
  }
  canDelete(id, state = GlobalState.get()) {
    return this.item(id).canDelete(state);
  }
  canGet(id, state = GlobalState.get()) {
    return this.item(id).canGet(state);
  }
  canCreate(state = GlobalState.get()) {
    if (!this.relationship.supportsCreate) {
      return false;
    }
    return AllowedReducer.get(state, this.getResourceId(true))?.create;
  }
  canAction(state = GlobalState.get(), action) {
    return AllowedReducer.get(state, this.getResourceId(true))?.[action];
  }
  requestCanAction(body, options, getPath, method, action) {
    return this.getApi().canAction(body, options, getPath, method, action);
  }
  convertToSort(attrSortModel, cxt) {
    return OpenApiSortConverterFactory.create(attrSortModel, cxt);
  }
  canConvertCondition(cnd, cxt) {
    const filterFormat = this?.api?.api?.options?.filterFormat || 'default';
    return OpenApiConditionFilterConverterFactory.get(filterFormat)?.get(cnd);
  }
  canConvertComparator(cmp, cxt) {
    const filterFormat = this?.api?.api?.options?.filterFormat || 'default';
    return OpenApiComparatorFilterConverterFactory.get(filterFormat)?.get(cmp);
  }
  isAttributeSorted = (attribute, sort) => {
    if (!attribute || !sort) {
      return false;
    }
    return sort.fields.some(field => {
      return field.attrName === attribute?.name;
    });
  };
  convertToFilter(cnd, cxt) {
    const filterFormat = this?.api?.api?.options?.filterFormat || 'default';
    const filter = OpenApiConditionFilterConverterFactory.get(filterFormat)?.create(cnd, {
      ...cxt,
      filterConverterFactory: OpenApiConditionFilterConverterFactory.get(filterFormat),
      comparatorConverterFactory: OpenApiComparatorFilterConverterFactory.get(filterFormat)
    });
    return filter;
  }
}